<template>
    <!-- NOTE: because of flowbite components, this component must be used on client only -->
    <button
        type="button"
        ref="userButtonElm"
        @click="onUserButtonClick"
        class="w-8 h-8 cursor-pointer">
        <i-tabler-loader-3
            v-if="!props.userInfoReady"
            class="w-8 h-8 text-gray-700 animate-spin"></i-tabler-loader-3>
        <i-tabler-user-circle
            v-if="props.userInfoReady && !isLogged"
            class="w-8 h-8 text-gray-500 hover:text-gray-700"
            :class="{ 'text-gray-700': isDropdownVisible }"
            data-tooltip-target="tooltip-sign-in"
            data-tooltip-trigger="hover"
            data-tooltip-placement="bottom"></i-tabler-user-circle>
        <img
            v-if="props.userInfoReady && isLogged && props.user?.user?.avatar"
            class="w-7 h-7 rounded-full shadow-inner grayscale hover:grayscale-0"
            :class="{ 'grayscale-0': isDropdownVisible }"
            :src="props.user?.user?.avatar" />
        <i-tabler-user-circle
            v-if="props.userInfoReady && isLogged && !props.user?.user?.avatar"
            class="w-8 h-8 rounded-full shadow-inner text-green-500 hover:text-green-700"></i-tabler-user-circle>
    </button>

    <div
        ref="userDropdownMenuElm"
        class="z-10 hidden bg-white divide-y divide-gray-100 rounded-lg shadow-xl dark:bg-gray-700 dark:divide-gray-600">
        <ul class="py-2 text-gray-700 dark:text-gray-200">
            <li>
                <a
                    v-if="isLogged"
                    :href="subscriptionUrl"
                    @click="onAccountClick"
                    target="_blank"
                    class="flex items-center gap-2 px-4 py-2 hover:bg-gray-100 dark:hover:bg-gray-600 dark:hover:text-white font-medium group"
                    ><i-tabler-user
                        class="w-5 h-5 text-gray-400 group-hover:text-violet-500"></i-tabler-user>
                    <span class="inline-flex flex-col">
                        <span>{{ $t('user.account_label') }}</span>
                        <span class="text-xs font-normal">{{
                            props.user?.user?.email
                        }}</span>
                    </span>
                </a>
            </li>
            <li>
                <a
                    v-if="isLogged && !isPaid"
                    href=""
                    @click.prevent="dropdown?.hide()"
                    target="_blank"
                    class="flex items-center gap-2 px-4 py-2 hover:bg-gray-100 dark:hover:bg-gray-600 relative dark:hover:text-white font-medium group"
                    data-modal-target="promocode-modal"
                    data-modal-toggle="promocode-modal"
                    ><i-tabler-gift
                        class="w-5 h-5 text-gray-400 group-hover:text-pink-500"></i-tabler-gift>
                    <span class="relative"
                        >{{ $t('user.promocode_label') }}
                        <span
                            v-if="gotValidPromocode"
                            class="-top-1 -end-2 absolute w-2.5 h-2.5 bg-green-500 border-2 border-white dark:border-gray-800 rounded-full"></span>
                    </span>
                </a>
            </li>
            <li>
                <a
                    v-if="isLogged"
                    href="#"
                    @click.prevent="onSignOut"
                    class="flex items-center gap-2 px-4 py-2 text-gray-700 hover:bg-gray-100 dark:hover:bg-gray-600 dark:text-gray-200 dark:hover:text-white font-medium group"
                    ><i-tabler-logout
                        class="w-5 h-5 text-gray-400 group-hover:text-orange-500"></i-tabler-logout>
                    {{ $t('user.sign_out_label') }}</a
                >
            </li>
        </ul>
    </div>

    <tooltip-bubble id="tooltip-sign-in">{{
        $t('user.sign_in_label')
    }}</tooltip-bubble>

    <promocode-modal
        v-if="isLogged && !isPaid"
        id="promocode-modal"
        :have-valid-promocode="gotValidPromocode"
        :user-email="props.user?.user?.email"
        @got-valid-promocode="onValidPromocode"
        @removed-promocode="onRemovedPromocode"></promocode-modal>
</template>

<script lang="ts" setup>
import type { PaywallUser } from '~/helpers/types';
import { Dropdown } from 'flowbite';
import type { DropdownOptions, DropdownInterface } from 'flowbite';
import type { InstanceOptions } from 'flowbite';
import { initTooltips, initModals } from 'flowbite';
import { getCustomerPortalUrl } from '~/helpers/utils';
import {
    getPromocode,
    setPromocode,
    validatePromocode,
} from '~/helpers/payments';

interface Props {
    user: PaywallUser | undefined;
    userInfoReady: boolean;
}

const props = withDefaults(defineProps<Props>(), {});

const subscriptionUrl = getCustomerPortalUrl();
const emit = defineEmits([
    'signed-in',
    'started-sign-out',
    'signed-out',
    'got-valid-promocode',
    'account-clicked',
]);
const isDropdownVisible = ref(false);
const gotValidPromocode = ref(false);

const isLogged = computed(() => {
    return props.user?.user?.id !== undefined;
});

const isPaid = computed(() => {
    return props.user?.paid;
});

const onUserButtonClick = () => {
    if (!isLogged.value) {
        // The promise will be resolved when user signed in
        window.paywall.open({ resolveEvent: 'signed-in' }).then(() => {
            dropdown?.hide();
            // Indicate that the user is logged in
            emit('signed-in');
        });
    }
};

const onValidPromocode = () => {
    gotValidPromocode.value = true;
    emit('got-valid-promocode');
};

const onRemovedPromocode = () => {
    gotValidPromocode.value = false;
};

const onSignOut = async () => {
    emit('started-sign-out');
    await window.paywall.signOut();
    emit('signed-out');
    dropdown?.hide();
    initTooltips();
};

const onAccountClick = (event: MouseEvent) => {
    dropdown?.hide();
    emit('account-clicked');
};

// set the dropdown menu element
const userButtonElm = ref<HTMLElement | null>(null);
// set the element that trigger the dropdown menu on click
const userDropdownMenuElm = ref<HTMLElement | null>(null);

// dropdown options
const options: DropdownOptions = {
    placement: 'bottom',
    triggerType: 'click',
    offsetSkidding: 0,
    offsetDistance: 10,
    delay: 300,
    onShow: (tooltip: DropdownInterface) => {
        isDropdownVisible.value = true;
    },
    onHide: (tooltip: DropdownInterface) => {
        isDropdownVisible.value = false;
    },
};

// dropdown instance options object
const instanceOptions: InstanceOptions = {
    id: 'userMenu',
    override: true,
};

let dropdown: DropdownInterface | null = null;

const confirmPromocode = async () => {
    const promocode = await getPromocode();
    if (promocode) {
        const valid = await validatePromocode(
            promocode,
            props.user?.user?.email
        );
        if (valid) {
            // it's valid
            gotValidPromocode.value = true;
            emit('got-valid-promocode');
        } else {
            // it's not valid, delete
            setPromocode('');
        }
    }
};

// watch userInfoReady
watch(
    () => props.userInfoReady,
    (newValue) => {
        if (newValue) {
            nextTick().then(() => {
                initTooltips();
                initModals();
            });
        }
    }
);

watch(
    () => props.user,
    (newValue) => {
        // we need user email for this
        confirmPromocode();
    }
);

onMounted(async () => {
    // dropdown
    dropdown = new Dropdown(
        userDropdownMenuElm.value,
        userButtonElm.value,
        options,
        instanceOptions
    );
    // tooltips
    initTooltips();
    // modals
    initModals();

    confirmPromocode();
});
</script>
